import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getCalls, createSubscriptionOffer } from '@/api/api';
import { Phone, Calendar, User, FileText, Plus, X } from 'lucide-react';
import { format } from 'date-fns';
import { tr } from 'date-fns/locale';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Label } from '@/components/ui/label';

// Sabit ürün listesi
const SUBSCRIPTION_PRODUCTS = [
  "Lisans Bedeli V18.01",
  "Turkcell M2M",
  "Vodafone M2M",
  "Türk Telekom M2M"
];

const SubscriptionSearches = () => {
  const navigate = useNavigate();
  const [calls, setCalls] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showOfferDialog, setShowOfferDialog] = useState(false);
  const [selectedCall, setSelectedCall] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  
  const [formData, setFormData] = useState({
    title: 'Arvento Abonelik Fiyat Teklifi',
    duration_months: 12,
    items: [{ product_name: '', quantity: 1, annual_price: '', discount_percentage: 0 }],
    notes: ''
  });

  useEffect(() => {
    loadCalls();
  }, []);

  const loadCalls = async () => {
    try {
      const data = await getCalls();
      // Sadece Abonelik aramalarını filtrele
      const subscriptionCalls = data.filter(call => call.call_type === 'Abonelik');
      setCalls(subscriptionCalls);
    } catch (error) {
      toast.error('Aramalar yüklenirken hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  // İndirimli fiyat hesapla
  const calculateDiscountedPrice = (item) => {
    const annualPrice = parseFloat(item.annual_price) || 0;
    const discountPercentage = parseFloat(item.discount_percentage) || 0;
    
    if (discountPercentage > 0) {
      return annualPrice * (1 - discountPercentage / 100);
    }
    return annualPrice;
  };

  // Ürün için toplam hesapla
  const calculateItemTotal = (item) => {
    const discountedPrice = calculateDiscountedPrice(item);
    const quantity = parseInt(item.quantity) || 1;
    const yearMultiplier = parseInt(formData.duration_months) / 12; // 12->1, 24->2, 36->3
    
    return discountedPrice * quantity * yearMultiplier;
  };

  // Toplam hesaplama
  const calculateTotals = () => {
    let subtotal = 0;
    formData.items.forEach(item => {
      subtotal += calculateItemTotal(item);
    });
    const vatAmount = subtotal * 0.20;
    const total = subtotal + vatAmount;
    return { subtotal, vatAmount, total };
  };

  // Ürün ekle
  const addItem = () => {
    setFormData({
      ...formData,
      items: [...formData.items, { product_name: '', quantity: 1, annual_price: '', discount_percentage: 0 }]
    });
  };

  // Ürün sil
  const removeItem = (index) => {
    if (formData.items.length > 1) {
      const newItems = formData.items.filter((_, i) => i !== index);
      setFormData({ ...formData, items: newItems });
    }
  };

  // Ürün güncelle
  const updateItem = (index, field, value) => {
    const newItems = [...formData.items];
    newItems[index] = { ...newItems[index], [field]: value };
    setFormData({ ...formData, items: newItems });
  };

  // Teklif oluştur butonuna tıklandığında
  const handleCreateOffer = (call, e) => {
    e.stopPropagation(); // Satıra tıklama eventini engelle
    setSelectedCall(call);
    setFormData({
      title: 'Abonelik Fiyat Teklifi',
      duration_months: 12,
      items: [{ product_name: '', quantity: 1, annual_price: '', discount_percentage: 0 }],
      notes: call.notes ? `Arama Notu: ${call.notes}` : ''
    });
    setShowOfferDialog(true);
  };

  // Teklif kaydet
  const handleSubmitOffer = async (e) => {
    e.preventDefault();
    
    // Ürün kontrolü
    const validItems = formData.items.filter(item => item.product_name && item.annual_price);
    if (validItems.length === 0) {
      toast.error('Lütfen en az bir ürün ekleyin');
      return;
    }

    setSubmitting(true);
    try {
      const offerData = {
        customer_id: selectedCall.customer_id,
        title: formData.title,
        duration_months: parseInt(formData.duration_months),
        items: validItems.map(item => ({
          product_name: item.product_name,
          quantity: parseInt(item.quantity) || 1,
          annual_price: parseFloat(item.annual_price),
          discount_percentage: parseFloat(item.discount_percentage) || 0
        })),
        notes: formData.notes
      };
      
      await createSubscriptionOffer(offerData);
      toast.success(`${selectedCall.customer_name} için abonelik teklifi oluşturuldu`);
      setShowOfferDialog(false);
      setSelectedCall(null);
      
      // Abonelik teklifleri sayfasına yönlendir
      navigate('/subscription-offers');
    } catch (error) {
      toast.error('Teklif oluşturulurken hata oluştu');
      console.error('Error creating offer:', error);
    } finally {
      setSubmitting(false);
    }
  };

  const totals = calculateTotals();

  if (loading) {
    return <div className="text-center py-12">Yükleniyor...</div>;
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-4xl font-bold progo-text mb-2">Abonelik Aramaları</h1>
        <p className="text-gray-600">Toplam {calls.length} arama kaydı</p>
      </div>

      <div className="bg-white rounded-xl shadow-lg p-6">
        {calls.length === 0 ? (
          <div className="text-center py-12">
            <Phone className="w-16 h-16 mx-auto text-blue-500 mb-4" />
            <h3 className="text-xl font-semibold text-gray-800 mb-2">Henüz Arama Kaydı Yok</h3>
            <p className="text-gray-600">
              Müşteri detay sayfalarından &quot;Arama Yap&quot; ile abonelik araması yapabilirsiniz.
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            {calls.map((call, index) => (
              <div 
                key={call.id} 
                className="border rounded-lg p-4 hover:bg-gray-50 transition-colors cursor-pointer" 
                onClick={() => navigate(`/customers/${call.customer_id}`)}
                data-testid={`call-${index}`}
              >
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    <span className="text-lg font-semibold text-gray-900 mb-2 block">
                      {call.customer_name}
                    </span>
                    <div className="space-y-1 text-sm text-gray-600">
                      <div className="flex items-center space-x-2">
                        <Calendar className="w-4 h-4" />
                        <span>{format(new Date(call.call_date), 'dd MMMM yyyy HH:mm', { locale: tr })}</span>
                      </div>
                      <div className="flex items-center space-x-2">
                        <User className="w-4 h-4" />
                        <span>Temsilci: {call.representative}</span>
                      </div>
                      {call.notes && (
                        <p className="text-gray-700 mt-2">
                          <strong>Notlar:</strong> {call.notes}
                        </p>
                      )}
                    </div>
                  </div>
                  <div className="flex flex-col items-end gap-2">
                    <span className="bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm font-medium">
                      Abonelik
                    </span>
                    <Button
                      onClick={(e) => handleCreateOffer(call, e)}
                      className="bg-orange-500 hover:bg-orange-600 text-white text-sm"
                      size="sm"
                    >
                      <FileText className="w-4 h-4 mr-1" />
                      Abonelik Teklifi Ver
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Teklif Oluştur Dialog */}
      <Dialog open={showOfferDialog} onOpenChange={setShowOfferDialog}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5 text-purple-600" />
              Abonelik Teklifi Oluştur
              {selectedCall && (
                <span className="ml-2 px-2 py-1 rounded text-sm font-semibold bg-blue-100 text-blue-800">
                  {selectedCall.customer_name}
                </span>
              )}
            </DialogTitle>
          </DialogHeader>
          
          <form onSubmit={handleSubmitOffer} className="space-y-4">
            {/* Müşteri Bilgisi */}
            {selectedCall && (
              <div className="p-3 bg-blue-50 rounded-lg border border-blue-200">
                <div className="flex items-center gap-2">
                  <User className="w-4 h-4 text-blue-600" />
                  <span className="font-medium text-blue-800">{selectedCall.customer_name}</span>
                </div>
              </div>
            )}

            {/* Teklif Başlığı */}
            <div>
              <Label>Teklif Başlığı</Label>
              <Input
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                placeholder="Abonelik Fiyat Teklifi"
              />
            </div>

            {/* Teklif Süresi */}
            <div>
              <Label>Teklif Süresi *</Label>
              <Select
                value={formData.duration_months?.toString()}
                onValueChange={(value) => setFormData({ ...formData, duration_months: parseInt(value) })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Süre Seçin" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="12">12 Ay</SelectItem>
                  <SelectItem value="24">24 Ay</SelectItem>
                  <SelectItem value="36">36 Ay</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Ürünler */}
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <Label className="text-base font-semibold">Ürünler *</Label>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={addItem}
                  className="text-purple-600 border-purple-300 hover:bg-purple-50"
                >
                  <Plus className="w-4 h-4 mr-1" />
                  Ürün Ekle
                </Button>
              </div>
              
              {formData.items.map((item, index) => {
                const discountedPrice = calculateDiscountedPrice(item);
                const itemTotal = calculateItemTotal(item);
                const yearMultiplier = parseInt(formData.duration_months) / 12;
                return (
                  <div key={index} className="p-4 bg-purple-50 rounded-lg border border-purple-200">
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium text-purple-700">Ürün {index + 1}</span>
                        {item.product_name && (
                          <span className="text-xs px-2 py-0.5 rounded bg-blue-100 text-blue-700">
                            İndirimli × Adet × {yearMultiplier} Yıl
                          </span>
                        )}
                      </div>
                      {formData.items.length > 1 && (
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => removeItem(index)}
                          className="text-red-500 hover:text-red-700 hover:bg-red-50 h-6 w-6 p-0"
                        >
                          <X className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                    
                    <div className="grid grid-cols-12 gap-3">
                      {/* Ürün Seçimi */}
                      <div className="col-span-4">
                        <Label className="text-xs">Ürün</Label>
                        <Select
                          value={item.product_name}
                          onValueChange={(value) => updateItem(index, 'product_name', value)}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Ürün Seçin" />
                          </SelectTrigger>
                          <SelectContent>
                            {SUBSCRIPTION_PRODUCTS.map((product) => (
                              <SelectItem key={product} value={product}>
                                {product}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      
                      {/* Adet */}
                      <div className="col-span-2">
                        <Label className="text-xs">Adet</Label>
                        <Input
                          type="number"
                          min="1"
                          value={item.quantity}
                          onChange={(e) => updateItem(index, 'quantity', parseInt(e.target.value) || 1)}
                        />
                      </div>
                      
                      {/* Yıllık Fiyat */}
                      <div className="col-span-2">
                        <Label className="text-xs">Yıllık Fiyat</Label>
                        <Input
                          type="number"
                          step="0.01"
                          placeholder="0.00"
                          value={item.annual_price}
                          onChange={(e) => updateItem(index, 'annual_price', e.target.value)}
                        />
                      </div>
                      
                      {/* İndirim Oranı (%) */}
                      <div className="col-span-2">
                        <Label className="text-xs">İndirim %</Label>
                        <Input
                          type="number"
                          min="0"
                          max="100"
                          step="0.1"
                          placeholder="0"
                          value={item.discount_percentage || ''}
                          onChange={(e) => updateItem(index, 'discount_percentage', parseFloat(e.target.value) || 0)}
                        />
                      </div>
                      
                      {/* İndirimli Fiyat (Otomatik Hesaplanan) */}
                      <div className="col-span-2">
                        <Label className="text-xs">İndirimli Fiyat</Label>
                        <div className="h-10 flex items-center px-3 bg-white rounded border text-sm font-semibold text-blue-600">
                          {discountedPrice.toFixed(2)} ₺
                        </div>
                      </div>
                    </div>
                    
                    {/* Satır Toplam */}
                    <div className="mt-3 pt-3 border-t border-purple-200 flex justify-end">
                      <div className="text-right">
                        <span className="text-xs text-gray-500 mr-2">Satır Toplam:</span>
                        <span className="text-lg font-bold text-green-600">{itemTotal.toFixed(2)} ₺</span>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Toplam Özeti */}
            <div className="p-4 bg-gray-50 rounded-lg border space-y-2">
              <div className="flex justify-between items-center text-sm">
                <span className="text-gray-600">Ara Toplam</span>
                <span className="font-semibold">{totals.subtotal.toFixed(2)} ₺</span>
              </div>
              <div className="flex justify-between items-center text-sm">
                <span className="text-gray-600">KDV (%20)</span>
                <span className="font-semibold text-orange-600">{totals.vatAmount.toFixed(2)} ₺</span>
              </div>
              <div className="border-t pt-2 flex justify-between items-center">
                <span className="font-bold text-lg">Genel Toplam (KDV Dahil)</span>
                <span className="text-2xl font-bold text-green-600">{totals.total.toFixed(2)} ₺</span>
              </div>
            </div>

            {/* Notlar */}
            <div>
              <Label>Notlar / Açıklamalar</Label>
              <Textarea
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                placeholder="Teklif ile ilgili ek bilgiler..."
                rows={3}
                className="bg-white border-gray-300 focus:border-purple-500 focus:ring-purple-500"
              />
            </div>

            {/* Buttons */}
            <div className="flex justify-end space-x-2 pt-4">
              <Button 
                type="button" 
                variant="outline" 
                onClick={() => {
                  setShowOfferDialog(false);
                  setSelectedCall(null);
                }}
              >
                İptal
              </Button>
              <Button 
                type="submit" 
                className="bg-purple-600 hover:bg-purple-700 text-white"
                disabled={submitting}
              >
                {submitting ? 'Oluşturuluyor...' : 'Teklif Oluştur'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SubscriptionSearches;
